import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallbackComponent?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - React Error Boundary to catch and handle JavaScript errors
 * Provides a fallback UI when an error occurs in the component tree
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  // This lifecycle method is called when an error occurs
  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  // This lifecycle method is called when an error occurs and provides error details
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console and potentially to error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // In production, you might want to log this to an error reporting service
    // Example: LoggingService.logError(error, errorInfo);
  }

  // Handle the retry action
  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback UI if provided
      if (this.props.fallbackComponent) {
        return this.props.fallbackComponent;
      }

      // Default fallback UI
      return (
        <div className="container my-5">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="card border-danger">
                <div className="card-header bg-danger text-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Something went wrong
                  </h5>
                </div>
                <div className="card-body">
                  <p className="card-text">
                    We're sorry, but something unexpected happened. This error has been logged 
                    and we'll work to fix it as soon as possible.
                  </p>
                  
                  {/* Show error details in development mode */}
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <div className="mt-3">
                      <details className="mb-3">
                        <summary className="btn btn-outline-secondary btn-sm">
                          Show Error Details (Development)
                        </summary>
                        <div className="mt-2 p-3 bg-light border rounded">
                          <h6>Error:</h6>
                          <pre className="small text-danger">
                            {this.state.error.toString()}
                          </pre>
                          {this.state.errorInfo && (
                            <>
                              <h6 className="mt-3">Component Stack:</h6>
                              <pre className="small">
                                {this.state.errorInfo.componentStack}
                              </pre>
                            </>
                          )}
                        </div>
                      </details>
                    </div>
                  )}
                  
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-primary" 
                      onClick={this.handleRetry}
                    >
                      <i className="fas fa-redo me-2"></i>
                      Try Again
                    </button>
                    <button 
                      className="btn btn-outline-secondary" 
                      onClick={() => window.location.href = '/'}
                    >
                      <i className="fas fa-home me-2"></i>
                      Go Home
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No error occurred, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary; 